import { useRef, useState } from 'react';
import type React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionStarterInfoTracking } from '#src/app/pages/petitionDetails/shared/hooks';

import { usePetitionStarterPhoto } from '../../hooks/petitionStarterPhoto';

type StarterPopoverProps = {
	photoUrl?: string | null;
	displayName: string;
	description?: string | null;
};

const STARTER_POPOVER_ID = 'petition-starter-popover';

type Petition = PetitionDetailsPageData['petition'];

type Props = {
	user: Petition['user'];
	organization: Petition['organization'];
	createdAt: Petition['createdAt'];
	createdAtLocalized: Petition['createdAtLocalized'];
	petitionPageStarterInHeaderEnabled: boolean;
};

type StarterClusterProps = {
	starterPhotoUrl?: string;
	displayName: string;
	hasDescription: boolean;
};

const StarterCluster: React.FC<StarterClusterProps> = ({ starterPhotoUrl, displayName, hasDescription }) => (
	<span className="flex min-w-0 items-center gap-3">
		{starterPhotoUrl && (
			<span className="shrink-0">
				<Avatar size={28} src={starterPhotoUrl} alt={displayName} />
			</span>
		)}
		<Text
			variant="body"
			size="small"
			fontWeight="bold"
			truncate
			className={cn('text-typography-inverse-strong min-w-0', { 'cursor-pointer hover:underline': hasDescription })}
		>
			{displayName}
		</Text>
	</span>
);

const StarterPopoverCard: React.FC<StarterPopoverProps> = ({ photoUrl, displayName, description }) => (
	<div
		id={STARTER_POPOVER_ID}
		role="tooltip"
		className="bg-typography-inverse-strong absolute bottom-full left-0 z-50 mb-3 w-max overflow-hidden rounded-xl shadow-xl"
	>
		<div className="flex items-center gap-3 p-3 pb-2">
			{photoUrl && <Avatar size={56} src={photoUrl} alt={displayName} />}
			<Text variant="body" size="large" fontWeight="bold" className="text-typography-strong">
				{displayName}
			</Text>
		</div>
		{description && (
			<Text as="p" variant="body" size="small" className="text-typography-weak max-w-xs px-3 pb-3">
				{description}
			</Text>
		)}
	</div>
);

// oxlint-disable-next-line complexity
export const PetitionStarterInHeader: React.FC<Props> = ({
	user,
	organization,
	createdAt,
	createdAtLocalized,
	petitionPageStarterInHeaderEnabled,
}) => {
	const { photoUrl: starterPhotoUrl } = usePetitionStarterPhoto();
	const locationLabel = user.formattedLocationString;
	const [showCard, setShowCard] = useState(false);
	const { trackStarterInfoClick } = usePetitionStarterInfoTracking();
	const hasTracked = useRef(false);
	const { localizeDate } = useI18n();

	if (!petitionPageStarterInHeaderEnabled) return null;
	if (organization) return null;
	if (user.anonymous) return null;

	const createdAtLabel = createdAtLocalized || (createdAt ? localizeDate(createdAt, 'date', 'long') : undefined);

	const showPopOver = () => {
		setShowCard(true);
		if (user.description && !hasTracked.current) {
			hasTracked.current = true;
			trackStarterInfoClick();
		}
	};

	return (
		<div className="flex items-center gap-3 pt-2" data-qa="petition-starter-in-header">
			<div
				className="relative flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
				tabIndex={user.description ? 0 : -1}
				aria-describedby={showCard && user.description ? STARTER_POPOVER_ID : undefined}
				onMouseEnter={showPopOver}
				onMouseLeave={() => setShowCard(false)}
				onFocus={showPopOver}
				onBlur={() => setShowCard(false)}
			>
				<StarterCluster
					starterPhotoUrl={starterPhotoUrl}
					displayName={user.displayName}
					hasDescription={!!user.description}
				/>
				{showCard && user.description && (
					<StarterPopoverCard
						photoUrl={starterPhotoUrl}
						displayName={user.displayName}
						description={user.description}
					/>
				)}
			</div>
			{locationLabel && (
				<>
					<span className="bg-typography-inverse-weak/60 h-3 w-px" aria-hidden />
					<Text variant="body" size="small" className="text-typography-inverse-strong/60">
						{locationLabel}
					</Text>
				</>
			)}
			{(createdAtLocalized || createdAt) && (
				<>
					<span className="bg-typography-inverse-weak/60 h-3 w-px" aria-hidden />
					<Text variant="body" size="small" className="text-typography-inverse-strong/60">
						<Translate
							value="corgi.petition_gamma.petition_starter_in_header.created"
							replacements={{ date: createdAtLabel }}
						/>
					</Text>
				</>
			)}
		</div>
	);
};
